import React, { useMemo } from "react";

import {
  snapToWholePeriods,
  SPEND_TIME_UNIT,
  SpendFilters,
} from "lib/hooks/api/spend/spendFilters";
import { useSpendOverTime } from "lib/hooks/api/spend/useSpendOverTime";
import { useTopSpendSuppliers } from "lib/hooks/api/spend/useTopSpendSuppliers";
import { TrackingProvider } from "lib/tracking";
import { SpendGroupBy } from "lib/types/graphQLEnums";
import { ORDER_BY, SORT_ORDER, SpendRankingSort } from "./hooks/useRankingTable";
import { SpendChartType } from "./hooks/useSpendUrlState";
import { SpendChartError } from "./pageStates";
import SpendChart from "./SpendChart";
import { buildGroupedSpendDatasets } from "./spendChartUtils";

type Props = {
  filters: SpendFilters;
  dateUnit: "month" | "quarter" | "year";
  /** The ranking's order: the chart plots its top ten, by the measure it ranks on. */
  sort: SpendRankingSort;
  /** The shape to draw. */
  chartType: SpendChartType;
};

function SupplierChart({ filters, dateUnit, sort, chartType }: Props) {
  const { measure } = sort;
  const hasDateRange = Boolean(filters.dateFrom || filters.dateTo);
  const {
    data: top10Suppliers,
    isError: isSuppliersError,
    error: suppliersError,
    refetch: refetchSuppliers,
  } = useTopSpendSuppliers(
    {
      filters,
      limit: 10,
      offset: 0,
      orderBy: ORDER_BY[sort.measure],
      sortOrder: SORT_ORDER[sort.sortOrder],
    },
    { enabled: hasDateRange },
  );
  const supplierGuids = top10Suppliers?.results
    .map((d) => d.supplierGuid)
    .filter((guid): guid is string => Boolean(guid));
  const hasSupplierGuids = Boolean(supplierGuids?.length);

  const {
    data: overTime,
    isLoading,
    isError: isOverTimeError,
    error: overTimeError,
    refetch: refetchOverTime,
  } = useSpendOverTime(
    {
      // Whole periods only, so a range starting mid-quarter doesn't chart its first
      // quarter as a dip. The ranking above still uses the range as set.
      filters: {
        ...snapToWholePeriods(filters, dateUnit),
        supplierGuids,
      },
      dateUnit: SPEND_TIME_UNIT[dateUnit],
      groupBy: SpendGroupBy.Supplier,
    },
    { enabled: hasDateRange && hasSupplierGuids },
  );

  const isError = isSuppliersError || isOverTimeError;
  const chartError = suppliersError ?? overTimeError;
  const onRetry = () => {
    void refetchSuppliers();
    void refetchOverTime();
  };

  const chartData = useMemo(
    () => (overTime ? buildGroupedSpendDatasets(overTime.points, measure) : []),
    [overTime, measure],
  );

  return (
    <TrackingProvider data={{ "Context source": "Supplier spending chart" }}>
      {isError ? (
        <SpendChartError error={chartError} onRetry={onRetry} />
      ) : (
        <SpendChart
          data={chartData}
          dateUnit={dateUnit}
          isLoading={isLoading}
          measure={measure}
          chartType={chartType}
        />
      )}
    </TrackingProvider>
  );
}

export default SupplierChart;
